import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  addItemProductRequest,
  addItemRequest,
  addItemSupplyRequest,
  getPackingOrderRequest,
  getPackingOrdersRequest,
  relabelRequest,
  removeItemRequest,
  removeItemSupplyRequest,
  sendToCourierRequest,
  updateItemRequest,
} from './operatorPackingApi';
import { packingItemOpened, packingItemOpening, packingResolutionDismissed } from './operatorPackingActions';
import {
  canSendPacking,
  currentItemOf,
  isPackingIdle,
  itemOf,
  itemsShown,
  isPackingProductCode,
  packingUnitOf,
  matchPackingLines,
  matchSupply,
  packingLineOption,
  packingOrderName,
  packingReloadedMessage,
  packingTapKey,
  packingWriteKey,
  printableLabels,
  productInItem,
  selectPackingUnconfirmed,
  supplyInItem,
} from './operatorPackingUtils';
import { matchCodes } from '../operator/operatorCodes';
import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { signalDismissed, signalRaised } from '../operator/operatorDeviceSlice';
import { OPERATOR_ERROR, isRetryable, localError, readOperatorError } from '../operator/operatorErrors';
import { idempotentThunkOptions, sendWrite } from '../operator/operatorIdempotency';
import { SCAN_GATE, cleanCode, scanGate } from '../operator/operatorScanGate';
import { isTaskOnScreen, offScreenMeta } from '../operator/operatorTaskScope';
import { selectCurrentOperatorWarehouseId } from '../operator/operatorUtils';
import { labelName, mergeLabelPdfs } from '../../services/pdfService';
import { toPublicLabelUrl } from '../../utils/s3Utils';

const warehouseOf = (getState) => selectCurrentOperatorWarehouseId(getState());

export const fetchPackingQueue = createAsyncThunk(
  'operatorPacking/fetchQueue',
  async ({ page = 1 } = {}, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await getPackingOrdersRequest(warehouseOf(getState), { page });
      return fulfillWithValue(data, { receivedAt: Date.now() });
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos cargar las órdenes por empacar.'));
    }
  }
);

export const fetchPackingOrder = createAsyncThunk(
  'operatorPacking/fetchOrder',
  async (orderId, { getState, rejectWithValue }) => {
    try {
      const { data } = await getPackingOrderRequest(warehouseOf(getState), orderId);
      return data;
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos abrir la orden.'));
    }
  }
);

/** The screen is behind the server, or what the write acted on is gone: the order is loaded again. */
const RELOADS_ORDER = [OPERATOR_ERROR.STALE, OPERATOR_ERROR.NOT_FOUND];

/** A 412 says why even without the server's words. */
const readPackingError = (error, fallback) => {
  const failure = readOperatorError(error, fallback);
  return failure.kind === OPERATOR_ERROR.STALE ? readOperatorError(error, 'La orden cambió mientras tanto.') : failure;
};

/** What the screen showed before an addition; left out when it is not known. */
const shown = (field, value) => (value != null ? { [field]: value } : {});

/**
 * A packing write. There is no idempotency key: an addition says what the
 * screen showed before it, so a write whose answer was lost goes again
 * exactly as it was (`retryLastPackingWrite`), and the server answers
 * `replayed` if it had landed. That only holds one write of an order at a
 * time, sent from its screen, and with nothing but the lost one sent after
 * it: the slice keeps, by order, the write out and the one unconfirmed — its
 * very `arg` —, and nothing else of that order gets through until they settle.
 *
 * Where the answer lands is read when it arrives. An order off screen gets
 * the band named after it (`meta.offScreenTask`) and reloads nothing. The
 * order on screen takes it only if its visit sent it; one sent on an
 * earlier visit reloads the order instead, so the screen shows how it
 * stands. A 412 or 404 reloads the order before the failure is told, so the
 * band can say whether the reload worked (`reloaded`). A removal sent again
 * that finds nothing had gone through the first time (`alreadyRemoved`).
 *
 * @param {string} typePrefix
 * @param {(warehouseId: number, arg: object) => Promise} send
 * @param {string} fallback
 * @param {{removal?: boolean}} [options]
 */
const packingWrite = (typePrefix, send, fallback, { removal = false } = {}) =>
  createAsyncThunk(
    typePrefix,
    async (arg, { getState, dispatch, requestId, rejectWithValue, fulfillWithValue }) => {
      const before = getState().operatorPacking;
      const retried = before.unconfirmed[arg.orderId]?.arg === arg;
      const name = packingOrderName(before.order);
      const offScreen = () => offScreenMeta(getState(), OPERATOR_FLOW.PACKING, arg.orderId, name);
      const awaited = () => getState().operatorPacking.pendingWrite?.requestId === requestId;
      try {
        const { data } = await send(warehouseOf(getState), arg);
        const meta = offScreen();
        if (!meta && !awaited()) dispatch(fetchPackingOrder(arg.orderId));
        return fulfillWithValue(data, meta);
      } catch (error) {
        const read = readPackingError(error, fallback);
        // A removal sent again that finds nothing had gone through the first time, on screen or not.
        const failure =
          removal && retried && read.kind === OPERATOR_ERROR.NOT_FOUND ? { ...read, alreadyRemoved: true } : read;
        const meta = offScreen();
        if (meta) return rejectWithValue(failure, meta);
        if (!RELOADS_ORDER.includes(failure.kind)) {
          if (!awaited()) dispatch(fetchPackingOrder(arg.orderId));
          return rejectWithValue(failure);
        }
        const reload = await dispatch(fetchPackingOrder(arg.orderId));
        return rejectWithValue({ ...failure, reloaded: reload.meta.requestStatus === 'fulfilled' }, offScreen());
      }
    },
    {
      condition: (arg, { getState }) => {
        const { orderId, order, pendingWrite, loading, inFlightByOrder, unconfirmed } = getState().operatorPacking;
        if (arg?.orderId !== orderId || order?.id !== orderId) return false;
        if (pendingWrite || loading || inFlightByOrder[orderId]) return false;
        return !unconfirmed[orderId] || unconfirmed[orderId].arg === arg;
      },
      // Keyed by what it acts on, so only that button says it is working.
      getPendingMeta: ({ arg }) => ({ pendingKey: packingWriteKey(arg) }),
    }
  );

/** @param {{orderId: number, itemsCount?: number}} arg - `itemsCount`: how many items the screen showed. */
export const addItem = packingWrite(
  'operatorPacking/addItem',
  (warehouseId, { orderId, itemsCount }) => addItemRequest(warehouseId, orderId, shown('items_count', itemsCount)),
  'No pudimos agregar el bulto.'
);

/** @param {{orderId: number, itemId: number, values: object}} arg - Only what changed; `null` clears a measure. */
export const saveItem = packingWrite(
  'operatorPacking/saveItem',
  (warehouseId, { itemId, values }) => updateItemRequest(warehouseId, itemId, values),
  'No pudimos guardar las medidas.'
);

/**
 * @param {{orderId: number, itemId: number, productCode: string, productId?: number, lineId?: number,
 *   quantity?: number, factor?: number, sku?: string, inItem?: number}} arg - `inItem`: the units of
 *   the product the screen showed in the item.
 */
export const addItemProduct = packingWrite(
  'operatorPacking/addProduct',
  (warehouseId, { itemId, productCode, lineId, quantity = 1, inItem }) =>
    addItemProductRequest(warehouseId, itemId, {
      product_code: productCode,
      quantity,
      ...(lineId ? { line_id: lineId } : {}),
      ...shown('in_item', inItem),
    }),
  'No pudimos agregar el producto al bulto.'
);

/** @param {{orderId: number, itemId: number}} arg */
export const removeItem = packingWrite(
  'operatorPacking/removeItem',
  (warehouseId, { itemId }) => removeItemRequest(warehouseId, itemId),
  'No pudimos quitar el bulto.',
  { removal: true }
);

/**
 * @param {{orderId: number, itemId: number, supplyId: number, supplyCode?: string, inItem?: number}} arg
 *   `inItem`: how much of the supply the screen showed in the item.
 */
export const addItemSupply = packingWrite(
  'operatorPacking/addSupply',
  (warehouseId, { itemId, supplyId, inItem }) =>
    addItemSupplyRequest(warehouseId, itemId, { supply_id: supplyId, quantity: 1, ...shown('in_item', inItem) }),
  'No pudimos agregar el insumo.'
);

/** @param {{orderId: number, supplyUseId: number}} arg */
export const removeItemSupply = packingWrite(
  'operatorPacking/removeSupply',
  (warehouseId, { supplyUseId }) => removeItemSupplyRequest(warehouseId, supplyUseId),
  'No pudimos quitar el insumo.',
  { removal: true }
);

/**
 * «Enviar al courier»: the packed order goes to its courier, which makes the
 * shipment and its labels in the background; the screen then reads the order
 * until they come (`operatorPackingListeners`). It cannot be taken back from
 * the phone. Unlike the packing writes it carries a key: sent again under it,
 * it is not sent twice, and while an attempt is out the server answers
 * without sending another. A refusal — `courier_blocker` says why the order
 * cannot go — loads the order on screen again.
 *
 * @param {{orderId: number, idempotencyKey?: string}} arg
 */
export const sendToCourier = createAsyncThunk(
  'operatorPacking/sendToCourier',
  async (arg, { getState, dispatch, requestId, rejectWithValue, fulfillWithValue }) => {
    const before = getState().operatorPacking;
    const name = before.order?.id === arg.orderId ? packingOrderName(before.order) : `Orden #${arg.orderId}`;
    const offScreen = () => offScreenMeta(getState(), OPERATOR_FLOW.PACKING, arg.orderId, name);
    try {
      const { data } = await sendToCourierRequest(warehouseOf(getState), arg.orderId, { idempotency_key: requestId });
      return fulfillWithValue(data, offScreen());
    } catch (error) {
      const failure = readOperatorError(error, 'No pudimos enviar la orden al courier.');
      const meta = offScreen();
      if (!meta && !isRetryable(failure)) dispatch(fetchPackingOrder(arg.orderId));
      return rejectWithValue(failure, meta);
    }
  },
  {
    ...idempotentThunkOptions,
    condition: (arg, thunkApi) => {
      if (!idempotentThunkOptions.condition(arg, thunkApi)) return false;
      const state = thunkApi.getState();
      // From the band of an order off screen it goes again under its key.
      if (!isTaskOnScreen(state, OPERATOR_FLOW.PACKING, arg?.orderId)) return true;
      // On screen, one action at a time. An attempt already out does not hold it: the band's «Reintentar»
      // of a lost send gets the server's answer of how it goes, and it sends nothing twice.
      return isPackingIdle(state.operatorPacking);
    },
    getPendingMeta: ({ arg }) => ({ pendingKey: arg?.orderId }),
  }
);

const courierFlow = ({ operatorPacking: packing }) => ({
  lastWrite: packing.courierSend.lastWrite,
  pending: packing.inFlightByOrder[packing.orderId] ?? null,
});

/** As its screen sends it: tapped again after a lost answer, it goes under the key it was first sent with. */
export const submitSendToCourier = (arg) => sendWrite(sendToCourier, arg, courierFlow);

/**
 * «Volver a crear el envío»: the shipment of an order whose items the floor
 * changed after it was labeled is created again at its courier, with the
 * items it has now. The server does it by itself once packing is done; this
 * is the packer's, when that one failed or never started. It goes as a send
 * does (`sendToCourier`): with a key, the screen then reading the order until
 * the new label comes, and a refusal loading the order again — its `relabel`
 * says why.
 *
 * @param {{orderId: number, idempotencyKey?: string}} arg
 */
export const requestRelabel = createAsyncThunk(
  'operatorPacking/requestRelabel',
  async (arg, { getState, dispatch, requestId, rejectWithValue, fulfillWithValue }) => {
    const before = getState().operatorPacking;
    const name = before.order?.id === arg.orderId ? packingOrderName(before.order) : `Orden #${arg.orderId}`;
    const offScreen = () => offScreenMeta(getState(), OPERATOR_FLOW.PACKING, arg.orderId, name);
    try {
      const { data } = await relabelRequest(warehouseOf(getState), arg.orderId, { idempotency_key: requestId });
      return fulfillWithValue(data, offScreen());
    } catch (error) {
      const failure = readOperatorError(error, 'No pudimos volver a crear el envío.');
      const meta = offScreen();
      if (!meta && !isRetryable(failure)) dispatch(fetchPackingOrder(arg.orderId));
      return rejectWithValue(failure, meta);
    }
  },
  {
    ...idempotentThunkOptions,
    condition: (arg, thunkApi) => {
      if (!idempotentThunkOptions.condition(arg, thunkApi)) return false;
      const state = thunkApi.getState();
      if (!isTaskOnScreen(state, OPERATOR_FLOW.PACKING, arg?.orderId)) return true;
      return isPackingIdle(state.operatorPacking);
    },
    getPendingMeta: ({ arg }) => ({ pendingKey: arg?.orderId }),
  }
);

const relabelFlow = ({ operatorPacking: packing }) => ({
  lastWrite: packing.relabel.lastWrite,
  pending: packing.inFlightByOrder[packing.orderId] ?? null,
});

/** As its screen sends it: tapped again after a lost answer, it goes under the key it was first sent with. */
export const submitRelabel = (arg) => sendWrite(requestRelabel, arg, relabelFlow);

const WRITES = {
  [addItem.typePrefix]: addItem,
  [saveItem.typePrefix]: saveItem,
  [addItemProduct.typePrefix]: addItemProduct,
  [removeItem.typePrefix]: removeItem,
  [addItemSupply.typePrefix]: addItemSupply,
  [removeItemSupply.typePrefix]: removeItemSupply,
};

/**
 * Sends again, with the very same body, the write left unconfirmed of the
 * order on screen; from anywhere else, nothing.
 */
export const retryLastPackingWrite = () => (dispatch, getState) => {
  const write = selectPackingUnconfirmed(getState());
  const thunk = write && WRITES[write.type];
  return thunk ? dispatch(thunk(write.arg)) : null;
};

/**
 * Loads the order on screen. A write left unconfirmed stays so: the lost one
 * may still land after this read, and only sending it again tells. Without
 * one, a band offering to resend has nothing left to send.
 *
 * @param {number} orderId
 */
export const loadPackingOrder = (orderId) => async (dispatch, getState) => {
  const action = await dispatch(fetchPackingOrder(orderId));
  const { operatorPacking, operatorDevice } = getState();
  if (!operatorPacking.unconfirmed[orderId] && operatorDevice.signal?.retry === OPERATOR_FLOW.PACKING) {
    dispatch(signalDismissed());
  }
  return action;
};

/** A scan of a product into an item, with its units the screen shows there. */
const productScan = (order, itemId, { productCode, productId, lineId, sku, factor }) => ({
  orderId: order.id,
  itemId,
  productCode,
  productId,
  lineId,
  sku,
  factor,
  quantity: 1,
  inItem: productInItem(order, itemId, productId),
});

/**
 * Puts something in the item on screen, opening the first item when the
 * order has none: scanning the first box or product is how packing starts.
 * The two go as one step (`packingItemOpening`): a held camera read waits
 * for both. `put` gets the order as it is by then, to count what the screen
 * shows.
 */
const intoCurrentItem = async (dispatch, getState, put) => {
  const packing = getState().operatorPacking;
  if (!canSendPacking(packing)) return null;
  const current = currentItemOf(packing.order, packing.currentItemId);
  if (current) return dispatch(put(packing.order, current.id));

  const { orderId } = packing;
  dispatch(packingItemOpening(orderId));
  const created = await dispatch(addItem({ orderId, itemsCount: itemsShown(packing.order) }));
  // Released right before the addition, in the same turn: nothing gets between them.
  dispatch(packingItemOpened(orderId));
  const now = getState().operatorPacking;
  // Only into an item this visit shows: one made for a visit already left is not its to fill.
  if (created.meta.requestStatus !== 'fulfilled' || !itemOf(now.order, created.payload.item_id)) return created;
  return dispatch(put(now.order, created.payload.item_id));
};

/** «Otro bulto»: a new empty item, saying how many the screen shows. */
export const addAnotherItem = () => (dispatch, getState) => {
  const packing = getState().operatorPacking;
  if (!canSendPacking(packing)) return null;
  return dispatch(addItem({ orderId: packing.orderId, itemsCount: itemsShown(packing.order) }));
};

/**
 * A code read while packing: a supply of the warehouse (a box fills the
 * item's measures) or a product of the order, one at a time into the item.
 * A code of two products asks which; several lines of one product are the
 * server's to fill, a box across them if it has to. A supply or a line
 * tapped on screen (`supplyId`, `lineId`) is that one, even when its code is
 * another's too.
 *
 * Each read of a product adds a unit, so the camera reading the same product
 * again (`again`: it left the view and came back) counts, as in the route.
 *
 * @param {{code: string, source: string, again?: boolean, supplyId?: number, lineId?: number}} read
 */
export const scanPackingCode = createAsyncThunk(
  'operatorPacking/scanCode',
  async ({ code, supplyId, lineId }, { getState, dispatch, rejectWithValue }) => {
    const { order } = getState().operatorPacking;
    const value = cleanCode(code);
    const tappedLine = lineId != null ? (order.lines || []).find((line) => line.line_id === lineId) : null;
    const supply =
      supplyId != null
        ? (order.supply_options || []).find((item) => item.id === supplyId)
        : lineId == null && matchSupply(order, value);
    if ((supplyId != null && !supply) || (lineId != null && !tappedLine)) {
      return rejectWithValue(localError('Ya no está en la orden: recárgala.'));
    }

    if (supply) {
      await intoCurrentItem(dispatch, getState, (current, itemId) =>
        addItemSupply({
          orderId: current.id,
          itemId,
          supplyId: supply.id,
          supplyCode: value,
          inItem: supplyInItem(current, itemId, supply.id),
        })
      );
      return { kind: 'supply' };
    }

    const lines = tappedLine
      ? [{ line: tappedLine, factor: matchCodes(tappedLine.codes, value)?.factor ?? 1 }]
      : matchPackingLines(order, value);
    if (lines.length === 0) {
      return rejectWithValue(localError(`${value} no es de esta orden ni un insumo de la bodega.`));
    }
    if (new Set(lines.map(({ line }) => line.product_id)).size > 1) {
      return { kind: 'choose', code: value, options: lines.map(packingLineOption) };
    }

    const [{ line, factor }] = lines;
    await intoCurrentItem(dispatch, getState, (current, itemId) =>
      addItemProduct(
        productScan(current, itemId, {
          productCode: value,
          productId: line.product_id,
          lineId: lines.length === 1 ? line.line_id : undefined,
          sku: line.sku,
          factor,
        })
      )
    );
    return { kind: 'product' };
  },
  {
    condition: (read, { getState }) => {
      const packing = getState().operatorPacking;
      const busy = !canSendPacking(packing) || Boolean(packing.resolution || packing.scanning);
      const gate = {
        busy,
        lastCode: packing.scan.lastCode,
        countsRereads: isPackingProductCode(packing.order, read.code),
        unitOf: (code) => packingUnitOf(packing.order, code),
      };
      return scanGate(gate, read) === SCAN_GATE.ACCEPT;
    },
    getPendingMeta: ({ arg }) => ({ pendingKey: packingTapKey(arg) }),
  }
);

/**
 * A choice that names a line or an item the screen does not show: the screen
 * is behind the server, so the order is loaded again instead of sending, and
 * the band says so once it is.
 */
const reloadInsteadOfChoosing = async (dispatch, orderId) => {
  dispatch(packingResolutionDismissed());
  const reload = await dispatch(fetchPackingOrder(orderId));
  dispatch(
    signalRaised(
      packingReloadedMessage({
        message: 'La orden cambió mientras tanto.',
        reloaded: reload.meta.requestStatus === 'fulfilled',
      })
    )
  );
  return reload;
};

/**
 * The line an ambiguous product code was. Chosen for a code read on the
 * phone, it goes into the item; chosen for the server, the same scan goes
 * again with the line, re-armed from it: its product, SKU, unit and what
 * the item shows of it.
 *
 * @param {number} lineId
 */
export const choosePackingLine = (lineId) => async (dispatch, getState) => {
  const packing = getState().operatorPacking;
  const { resolution, order } = packing;
  if (!resolution || resolution.kind !== 'ambiguous' || !canSendPacking(packing)) return null;

  const line = (order.lines || []).find((item) => item.line_id === lineId);
  const fromServer = resolution.origin === 'server';
  if (!line || (fromServer && !itemOf(order, resolution.arg.itemId))) {
    return reloadInsteadOfChoosing(dispatch, packing.orderId);
  }

  const option = resolution.options.find((item) => item.id === lineId);
  const scan = {
    productCode: fromServer ? resolution.arg.productCode : resolution.code,
    productId: line.product_id,
    lineId,
    sku: line.sku,
    factor: option?.factor ?? 1,
  };
  // The add that follows clears the sheet as it starts.
  if (fromServer) return dispatch(addItemProduct(productScan(order, resolution.arg.itemId, scan)));
  return intoCurrentItem(dispatch, getState, (current, itemId) => addItemProduct(productScan(current, itemId, scan)));
};

/** "+1": the last product once more, into the item on screen, as a new action. */
export const repeatLastPackedProduct = () => (dispatch, getState) => {
  const { lastProduct } = getState().operatorPacking;
  if (!lastProduct) return null;
  return intoCurrentItem(dispatch, getState, (current, itemId) =>
    addItemProduct(productScan(current, itemId, lastProduct))
  );
};

/** The items whose label could not be read: `failed` names each by its file, in the order they were sent. */
const unreadableItems = (sources, itemIndexes, failed) => {
  let next = 0;
  return sources.reduce((items, source, index) => {
    if (next >= failed.length || failed[next].name !== labelName(source)) return items;
    next += 1;
    return [...items, itemIndexes[index]];
  }, []);
};

/**
 * The labels of the order on screen, merged on the phone into one PDF and
 * opened in a new tab, as deliveries does. Each is fetched where the browser
 * may read it (`toPublicLabelUrl`); one that cannot be read is left out and
 * named by its item, and one only in ZPL never goes in. The tab opens once
 * the labels are in, so a popup blocker may stop it (`opened: false`): the
 * screen then offers the PDF as a link.
 *
 * One at a time, and only for the visit that asked: once the operator moved
 * on, or the order's labels changed, no PDF is made and nothing is said. Each
 * PDF made revokes the one before, whatever order it was of, so only one is
 * ever kept alive.
 *
 * @param {{orderId: number}} arg
 */
export const printPackingLabels = createAsyncThunk(
  'operatorPacking/printLabels',
  async ({ orderId }, { getState, requestId, rejectWithValue, fulfillWithValue }) => {
    const { order } = getState().operatorPacking;
    const { urls, itemIndexes, zplOnly } = printableLabels(order.labels);
    const sources = urls.map(toPublicLabelUrl);
    // Read once the labels are in: the operator may have moved on meanwhile.
    const elsewhere = () => {
      const state = getState();
      const offScreen = offScreenMeta(state, OPERATOR_FLOW.PACKING, orderId, packingOrderName(order));
      if (offScreen) return offScreen;
      return state.operatorPacking.labelsPrint.requestId === requestId ? undefined : { earlierVisit: true };
    };

    let merged;
    try {
      merged = await mergeLabelPdfs(sources);
    } catch (error) {
      return rejectWithValue({ kind: OPERATOR_ERROR.REFUSED, status: null, message: error.message }, elsewhere());
    }

    const report = {
      orderId,
      total: sources.length,
      included: sources.length - merged.failed.length,
      unreadable: unreadableItems(sources, itemIndexes, merged.failed),
      zplOnly,
    };
    const meta = elsewhere();
    if (meta) return fulfillWithValue({ ...report, url: null, opened: false }, meta);

    const previous = getState().operatorPacking.labelsPdfUrl;
    const url = URL.createObjectURL(merged.blob);
    const opened = Boolean(window.open(url, '_blank'));
    if (previous) URL.revokeObjectURL(previous);
    return { ...report, url, opened };
  },
  {
    condition: ({ orderId } = {}, { getState }) => {
      const { orderId: onScreen, order, labelsPrint } = getState().operatorPacking;
      if (orderId !== onScreen || order?.id !== orderId || labelsPrint.status === 'pending') return false;
      return printableLabels(order.labels).urls.length > 0;
    },
    // Keyed by its order, so the button of another order does not say it is working.
    getPendingMeta: ({ arg }) => ({ pendingKey: arg.orderId }),
  }
);
